
import { ReactNode } from "react";
import { motion } from "framer-motion";
import { useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";

interface ADUFeatureBoxProps {
  title: string;
  icon: ReactNode;
  isActive: boolean;
  onClick: () => void;
  description?: string;
}

export const ADUFeatureBox = ({ 
  title, 
  icon, 
  isActive, 
  onClick, 
  description 
}: ADUFeatureBoxProps) => {
  let theme = "dark";
  try {
    const themeContext = useNeumorphicTheme();
    theme = themeContext.theme;
  } catch {
    // Not within NeumorphicThemeProvider, use default
  }

  const getBoxStyles = () => {
    if (isActive) {
      return 'bg-[#e4683b] text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#0d0d0d]';
    }
    return theme === "dark"
      ? 'bg-[#0a0a0a] text-white shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#0d0d0d] hover:shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#0d0d0d]'
      : 'bg-white text-gray-900 shadow-[6px_6px_12px_#c0c0c0,-6px_-6px_12px_#ffffff] hover:shadow-[8px_8px_16px_#c0c0c0,-8px_-8px_16px_#ffffff]';
  };

  const getTitleColor = () => {
    if (isActive) return 'text-white';
    return theme === "dark" ? 'text-white' : 'text-gray-900';
  };

  const getDescriptionColor = () => {
    if (isActive) return 'text-white/90';
    return theme === "dark" ? 'text-gray-400' : 'text-gray-600';
  };

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={`
        relative cursor-pointer p-4 md:p-6 rounded-xl transition-all duration-300
        ${getBoxStyles()}
      `}
      onClick={onClick}
    >
      <div className="flex flex-col items-center text-center space-y-2 md:space-y-3">
        <div className={`text-2xl md:text-3xl ${isActive ? 'text-white' : 'text-[#e4683b]'}`}>
          {icon}
        </div>
        <h3 className={`font-semibold text-sm md:text-lg ${getTitleColor()}`}>
          {title}
        </h3>
        {description && (
          <p className={`text-xs md:text-sm ${getDescriptionColor()}`}>
            {description}
          </p>
        )}
      </div>
    </motion.div>
  );
};
